import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Select from '../../shared/reactSelect/select.component';
import PeriodDropdown from '../../shared/periodDropdown.component';
import SecondaryMenu from '../../menu/secondaryMenu.component';
import {
  selectTaxYearForTaxEntries,
  selectCompanyCodeForTaxEntries,
  selectPeriodForTaxEntries,
  selectCountryTaxTypeForTaxEntries,
  fetchTaxEntryData,
  selectBookTypeForTaxEntries,
  fetchInitialDataForViewTaxEntryPage,
  resetTaxEntries,
} from '../store/actions';
import {
  countryTaxTypeSelector,
  countryTaxTypesOptionsSelector,
  isTaxEntriesLoadingSelector,
} from '../store/selectors';
import {
  subledgerFlagCompanyCodesSelector,
  taxYearsWithAllOptionsSelector,
  periodsSelector,
} from '../../shared/store/selectors';
import TaxEntriesGrid from './taxEntriesGrid.component';
import TaxEntry from './taxEntry.component';
import SubledgerJournalsPropTypes from './subledgerJournals.propTypes';
import GLJournalsPropTypes from './glJournals.propTypes';
import NoDataToDisplay from '../../shared/noDataToDisplay.component';
import { attachmentPropType, taxEntryPropType } from './propTypes';

class ViewTaxEntriesPage extends PureComponent {
  static propTypes = {
    bookType: PropTypes.string,
    companyCode: PropTypes.string,
    companyCodes: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    period: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    periods: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    taxYear: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string.isRequired,
    }),
    taxYears: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    selectTaxYearForTaxEntries: PropTypes.func.isRequired,
    selectCompanyCodeForTaxEntries: PropTypes.func.isRequired,
    selectPeriodForTaxEntries: PropTypes.func.isRequired,
    fetchInitialDataForViewTaxEntryPage: PropTypes.func.isRequired,
    resetTaxEntries: PropTypes.func.isRequired,
    countryTaxTypes: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })),
    countryTaxType: PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
    selectCountryTaxTypeForTaxEntries: PropTypes.func.isRequired,
    selectBookTypeForTaxEntries: PropTypes.func.isRequired,
    taxEntries: PropTypes.arrayOf(taxEntryPropType),
    taxEntriesLoading: PropTypes.bool.isRequired,
    fetchTaxEntryData: PropTypes.func.isRequired,
    taxEntryData: PropTypes.shape({
      subledgerJournals: SubledgerJournalsPropTypes,
      GLJournals: GLJournalsPropTypes,
      attachments: PropTypes.arrayOf(attachmentPropType).isRequired,
    }).isRequired,
    taxEntryId: PropTypes.number.isRequired,
    statCalc: PropTypes.string,
    companyCodeDetailsError: PropTypes.string,
  };

  componentDidMount() {
    const {
      companyCode,
      companyCodes: filteredCompanyCodes,
      fetchInitialDataForViewTaxEntryPage,
    } = this.props;

    this.selectTaxYear({ label: 'ALL', value: '-1' });

    const correctCompanyCodeIsPersistedInStore = (
      filteredCompanyCodes && filteredCompanyCodes.find(
        ({ value }) => value === companyCode
      )
    );

    fetchInitialDataForViewTaxEntryPage({
      isCorrectCompanyCodeSelected: correctCompanyCodeIsPersistedInStore,
    });
  }

  componentWillUnmount() {
    this.props.resetTaxEntries();
  }

  selectPeriod = selectedPeriod => {
    const { periods } = this.props;
    const value = selectedPeriod ? selectedPeriod.value : null;
    const period = periods.find(period => period.name === value);

    this.props.selectPeriodForTaxEntries(period);
  };

  selectCompanyCode = companyCode => {
    const value = companyCode ? companyCode.value : null;
    this.props.selectCompanyCodeForTaxEntries(value);
  };

  selectTaxYear = selectedTaxYear => {
    const { taxYears } = this.props;
    const value = selectedTaxYear ? selectedTaxYear.value : null;
    const taxYear = taxYears.find(taxYear => taxYear.value === value);
    const taxYearToSelect = taxYear ? {
      name: taxYear.label,
      value: taxYear.value,
    } : null;
    this.props.selectTaxYearForTaxEntries(taxYearToSelect);
  };

  selectCountryTaxType = selectedCountryTaxType => {
    const { countryTaxTypes } = this.props;
    const value = selectedCountryTaxType ? selectedCountryTaxType.value : null;
    const countryTaxType = countryTaxTypes.find(taxType => taxType.value === value);
    this.props.selectCountryTaxTypeForTaxEntries(countryTaxType);
  };

  selectBookType = selectedBookType => {
    this.props.selectBookTypeForTaxEntries(selectedBookType);
  };

  render() {
    const {
      bookType,
      companyCode,
      companyCodes,
      period,
      taxYear,
      taxYears,
      countryTaxTypes,
      countryTaxType,
      taxEntries,
      taxEntriesLoading,
      fetchTaxEntryData,
      taxEntryData,
      taxEntryId,
      statCalc,
      companyCodeDetailsError,
    } = this.props;

    const taxEntry = taxEntries.find(taxEntry => taxEntry.taxEntryId === taxEntryId);

    const selectedTaxYear = taxYear ? taxYear.value : '';
    const selectedCountryTaxType = countryTaxType ? countryTaxType.value : '';

    return (
      <div className="view-tax-entries-page">
        <SecondaryMenu>
          <PeriodDropdown
            onChange={this.selectPeriod}
          />
          <Select
            className="select-company"
            clearable={false}
            disabled={!period}
            name="companyCode"
            options={companyCodes}
            onChange={this.selectCompanyCode}
            scrollMenuIntoView={false}
            value={companyCode}
            virtualized
          />
          <div className="secondary-menu-label">Tax Years</div>
          <Select
            className="select-tax-years"
            clearable={false}
            name="taxYear"
            options={taxYears}
            onChange={this.selectTaxYear}
            scrollMenuIntoView={false}
            value={selectedTaxYear}
          />
          <div className="secondary-menu-label">Country Tax Type</div>
          <Select
            className="select-country-tax-type"
            clearable={false}
            disabled={!companyCode && !taxYear}
            name="countryTaxType"
            options={countryTaxTypes}
            onChange={this.selectCountryTaxType}
            scrollMenuIntoView={false}
            value={selectedCountryTaxType}
          />
        </SecondaryMenu>
        <div className="page">
          {!companyCode && !companyCodeDetailsError ? <NoDataToDisplay header={'Provision does not exist in the selected period.'} /> : null}
          {companyCodeDetailsError
            ? <NoDataToDisplay header={'Provision does not exist in the selected period.'} />
            : (
              <div className="container-fluid">
                <div className="row no-gutters">
                  <div className="col">
                    {!statCalc || !countryTaxType ? null : <TaxEntriesGrid
                      bookType={bookType}
                      countryTaxType={countryTaxType}
                      statCalc={statCalc}
                      fetchTaxEntryData={fetchTaxEntryData}
                      taxEntries={taxEntries}
                      taxEntriesLoading={taxEntriesLoading}
                      selectBookType={this.selectBookType}
                    />}
                  </div>
                </div>
                <div className="row no-gutters">
                  <div className="col">
                    {!!taxEntry && <TaxEntry taxEntry={taxEntry} taxEntryData={taxEntryData} />}
                  </div>
                </div>
              </div>
            )
          }
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { balanceSheetManagement, provision, shared } = state;

  return {
    bookType: shared.bookType,
    companyCode: shared.companyCode,
    companyCodes: subledgerFlagCompanyCodesSelector(state),
    period: shared.period,
    periods: periodsSelector(state),
    taxYear: shared.taxYear,
    taxYears: taxYearsWithAllOptionsSelector(state),
    countryTaxTypes: countryTaxTypesOptionsSelector(state),
    countryTaxType: countryTaxTypeSelector(state),
    taxEntries: balanceSheetManagement.shared.taxEntries,
    taxEntriesLoading: isTaxEntriesLoadingSelector(state),
    taxEntryData: balanceSheetManagement.shared.taxEntryData,
    taxEntryId: balanceSheetManagement.shared.taxEntryId,
    statCalc: provision.general.companyCodeDetails.statCalc,
    companyCodeDetailsError: provision.general.companyCodeDetailsError,
  };
}

export default connect(
  mapStateToProps,
  {
    selectTaxYearForTaxEntries,
    selectCompanyCodeForTaxEntries,
    selectPeriodForTaxEntries,
    selectCountryTaxTypeForTaxEntries,
    selectBookTypeForTaxEntries,
    fetchTaxEntryData,
    fetchInitialDataForViewTaxEntryPage,
    resetTaxEntries,
  }
)(ViewTaxEntriesPage);
